<template>
  <div class="w-full" :key="note.updated_at">
    <h1 class="mb-8 font-bold text-3xl">
      <inertia-link
        class="text-indigo-400 hover:text-indigo-600"
        :href="route('delivery-notes.index')"
        >Dispatch Notes</inertia-link>
      <span class="text-indigo-400 font-medium">/</span>
      <a
        class="text-indigo-400 hover:text-indigo-600"
        :href="route('customer.orders.show', note.order_id)"
        target="_blank"
      >
        Order #{{ note.order_id }}
      </a>
      <span class="text-indigo-400 font-medium">/</span>
      Note #{{ note.id }}
    </h1>

    <div class="xl:-mx-4 mb-4">

        <div class="bg-white shadow overflow-hidden sm:rounded-lg px-4 py-5 sm:px-6 ">
          <div class="flex flex-col justify-between xl:flex-row gap-y-2">
            <div>Customer: {{ note.order.customer_account.name }}</div>
            <div>Customer Contact: {{ note.order.customer_contact_number }}</div>
          </div>
        </div>
    </div>

    <div class="flex flex-col xl:flex-row xl:-mx-4">
      <div class="w-full">
        <div class="bg-white shadow overflow-hidden sm:rounded-lg">
          <div class="px-4 py-5 border-b border-gray-200 sm:px-6">

            <div>
              <dl>
                <div class="divide-y divide-gray-100">
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Note</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{{ note.id }}</dd>
                  </div>
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Order Number</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{{ note.order_id }}</dd>
                  </div>
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Location</dt>
                    <dd class="mt-1 text-sm leading-6 capitalize text-gray-700 sm:col-span-2 sm:mt-0 capitalize">
                      <template v-if="note.type === 'delivery'">
                        {{ note.delivery_location || 'N/A' }}
                      </template>
                      <template v-else>
                        Collection
                      </template>
                    </dd>
                  </div>
                  <div v-if="note.delivery_location !== 'island'" class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Address</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 capitalize">{{ note.delivery_address || 'N/A' }}</dd>
                  </div>
                  <template v-else>
                    <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt class="text-sm font-medium leading-6 text-gray-900">Vessel</dt>
                      <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 capitalize">{{ note.vessel }}</dd>
                    </div>
                    <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt class="text-sm font-medium leading-6 text-gray-900">Vessel Location</dt>
                      <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 capitalize">{{ note.vessel_location }}</dd>
                    </div>
                    <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                      <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Contact</dt>
                      <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 capitalize">{{ note.delivery_contact_number }}</dd>
                    </div>
                  </template>
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Date</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 capitalize">{{ note.delivery_date | localDate }}</dd>
                  </div>
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Type</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 capitalize">{{ note.type }}</dd>
                  </div>
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Status</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0 capitalize">
                      <Badge
                        class="px-3 py-1.5 rounded-lg"
                        :variant="
                          note.status === 'completed'
                          ? 'green'
                          : note.status === 'in-progress'
                          ? 'blue'
                          : note.status === 'void'
                          ? 'red'
                          : 'gray'
                        "
                      >
                        {{ note.status }}
                      </Badge>
                    </dd>
                  </div>
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Order Status</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                      <Badge
                        :variant="note.order.status === 'completed'
                        ? 'green'
                        : note.order.status === 'delivery-in-progress'
                        ? 'blue'
                        : 'gray'"
                      >
                        {{ note.order.status_label }}
                      </Badge>
                    </dd>
                  </div>
                  <div class="px-4 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                    <dt class="text-sm font-medium leading-6 text-gray-900">Delivery Team</dt>
                    <dd class="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                      <template v-if="note.team">
                        {{ note.team.name }}
                      </template>
                      <template v-else>
                        <AssignTeamModal
                          v-if="!note.team_id && note.status !== 'void'"
                          v-slot="{open}"
                          :note="note"
                        >
                          <button class="btn-primary px-3 py-1.5" @click="open" type="button">Assign a Team</button>
                        </AssignTeamModal>
                        <template v-else>
                          N/A
                        </template>
                      </template>
                    </dd>
                  </div>
                </div>
                <div class="px-4 py-2 sm:px-0">
                  <dt class="text-sm font-medium leading-6 text-gray-900">Items</dt>
                  <dd class="mt-2 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                    <div class="-mx-4 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg">
                      <table class="min-w-full divide-y divide-gray-300">
                        <thead>
                        <tr class="divide-x divide-gray-300">
                          <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Item Code</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">Description</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">Quantity</th>
                          <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">Unit</th>
                        </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-300">
                        <tr v-for="item in note.items" :key="'note-'+note.id+'-item-'+item.id" class="divide-x divide-gray-300">
                          <td class="relative py-4 pl-4 pr-3 text-sm sm:pl-6">
                            {{ item.item_code }}
                          </td>
                          <td class="px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
                            {{ item.description }}
                          </td>
                          <td class="px-3 py-3.5 text-sm text-gray-500 text-left">
                            {{ item.quantity_assigned }}
                          </td>
                          <td class="px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
                            {{ item.unit }}
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </dd>
                </div>
                <div class="px-4 py-2 sm:px-0">
                  <div class="flex w-full gap-x-3 justify-end">
                    <template v-if="note.status !== 'completed' && note.status !== 'void'">
                      <template v-if="note.status === 'pending'">
                        <AssignTeamModal
                          v-if="!note.team_id"
                          v-slot="{open}"
                          :note="note"
                        >
                          <button class="btn-primary" @click="open" type="button">Assign a Team</button>
                        </AssignTeamModal>
                      </template>
                      <HandoverNoteModal
                        v-slot="{open}"
                        :note="note"
                      >
                        <button
                          class="btn-primary"
                          @click="open"
                          :disabled="!canIssueItem"
                        >
                          <template v-if="!note.goods_collected_for_delivery_at">
                            Issue Items
                          </template>
                          <template v-else>
                            Handover Items
                          </template>
                        </button>
                      </HandoverNoteModal>
                      <button
                        v-if="canAdmin('void-delivery-note') && note.status !== 'in-progress'"
                        @click="voidNote"
                        class="btn-red">Void</button>
                    </template>
                    <a :href="route('delivery-notes.print-schedule', {
                      note: note.id,
                      order: note.order_id,
                    })" class="btn-primary" target="_blank">Print</a>
                  </div>
                </div>
              </dl>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Layout from "@/Shared/Admin/Layout.vue";
import AssignTeamModal from "./AssignTeamModal.vue";
import HandoverNoteModal from "./HandoverNoteModal.vue";
import Badge from "../../../Shared/Badge.vue";

export default {
  name: 'ShowNote',
  components: {Badge, HandoverNoteModal, AssignTeamModal},
  metaInfo: {
    title: 'Delivery Note',
  },
  layout: Layout,

  props: {
    note: Object,
  },

  data() {
    return {
      form: this.$inertia.form({})
    }
  },

  computed: {
    canIssueItem() {
      let can = false;
      const note = this.note
      if (Object.keys(note).length) {
        if (note.type === 'delivery' && note.team_id) {
          can = true
        } else if (note.type === 'collection') {
          can = true
        }
      }
      return can
    }
  },

  methods: {
    voidNote() {
      if (confirm('Void this delivery note?')) {
        this.form.delete(this.route('delivery-notes.void', {
          note: this.note.id,
          order: this.note.order_id,
        }))
      }
    }
  }
}
</script>
